import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function Reports() {
  const { API } = useContext(AuthContext);
  const [ledger, setLedger] = useState([]);
  const [balance, setBalance] = useState({ rows: [], totals: {} });
  const [incomeStatement, setIncomeStatement] = useState({});
  const [loading, setLoading] = useState(true);

  // Date Filters
  const [startDate, setStartDate] = useState(new Date().getFullYear() + '-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [ledgerRes, balanceRes, incomeRes] = await Promise.all([
        axios.get(`${API}/reports/ledger`),
        axios.get(`${API}/reports/balance-8-columns`, { params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API}/reports/income-statement`, { params: { start_date: startDate, end_date: endDate } })
      ]);
      setLedger(ledgerRes.data);
      setBalance(balanceRes.data);
      setIncomeStatement(incomeRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  const exportToCSV = (data, filename) => {
    toast.info('Función de exportación en desarrollo');
  };

  if (loading) {
    return <div className="p-8">Cargando reportes...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="reports-page">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold">Reportes Contables</h1>
          <p className="text-muted-foreground">Estados financieros y balance tributario</p>
        </div>

        <div className="flex gap-4 items-end bg-card p-4 rounded-lg border shadow-sm">
          <div className="space-y-2">
            <label className="text-sm font-medium">Desde</label>
            <input
              type="date"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Hasta</label>
            <input
              type="date"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <Button onClick={loadReports}>Actualizar</Button>
        </div>
      </div>

      <Tabs defaultValue="balance-8" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="balance-8">Balance 8 Columnas</TabsTrigger>
          <TabsTrigger value="income">Estado de Resultados</TabsTrigger>
          <TabsTrigger value="ledger">Libro Mayor</TabsTrigger>
        </TabsList>

        <TabsContent value="balance-8">
          <Card className="overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Balance General de 8 Columnas</CardTitle>
                  <CardDescription>Periodo: {startDate} al {endDate}</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => exportToCSV(balance, 'balance8.csv')}>
                  <Download className="mr-2 h-4 w-4" /> Exportar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="border p-2 text-left" rowSpan={2}>Cuenta</th>
                    <th className="border p-2 text-center" colSpan={2}>Sumas</th>
                    <th className="border p-2 text-center" colSpan={2}>Saldos</th>
                    <th className="border p-2 text-center" colSpan={2}>Inventario</th>
                    <th className="border p-2 text-center" colSpan={2}>Resultado</th>
                  </tr>
                  <tr>
                    <th className="border p-2 text-right w-24">Débito</th>
                    <th className="border p-2 text-right w-24">Crédito</th>
                    <th className="border p-2 text-right w-24">Deudor</th>
                    <th className="border p-2 text-right w-24">Acreedor</th>
                    <th className="border p-2 text-right w-24">Activo</th>
                    <th className="border p-2 text-right w-24">Pasivo</th>
                    <th className="border p-2 text-right w-24">Pérdida</th>
                    <th className="border p-2 text-right w-24">Ganancia</th>
                  </tr>
                </thead>
                <tbody>
                  {balance.rows.map((row) => (
                    <tr key={row.code} className="hover:bg-muted/50 border-b">
                      <td className="border p-2">
                        <div className="font-mono font-bold">{row.code}</div>
                        <div className="truncate w-48" title={row.name}>{row.name}</div>
                      </td>
                      <td className="border p-2 text-right font-mono">{row.debit_sum > 0 ? formatCurrency(row.debit_sum) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.credit_sum > 0 ? formatCurrency(row.credit_sum) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.debtor_balance > 0 ? formatCurrency(row.debtor_balance) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.creditor_balance > 0 ? formatCurrency(row.creditor_balance) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.asset > 0 ? formatCurrency(row.asset) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.liability > 0 ? formatCurrency(row.liability) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.loss > 0 ? formatCurrency(row.loss) : '-'}</td>
                      <td className="border p-2 text-right font-mono">{row.gain > 0 ? formatCurrency(row.gain) : '-'}</td>
                    </tr>
                  ))}
                  <tr className="bg-muted font-bold border-t-2 border-primary">
                    <td className="border p-2 text-right">TOTALES</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.debit_sum || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.credit_sum || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.debtor_balance || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.creditor_balance || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.asset || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.liability || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.loss || 0)}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(balance.totals?.gain || 0)}</td>
                  </tr>
                  <tr className="bg-green-50 font-bold text-green-800">
                    <td className="border p-2 text-right">RESULTADO DEL EJERCICIO</td>
                    <td className="border p-2" colSpan={4}></td>
                    <td className="border p-2 text-right font-mono">
                      {balance.totals?.asset > balance.totals?.liability ? formatCurrency(balance.totals.asset - balance.totals.liability) : '-'}
                    </td>
                    <td className="border p-2 text-right font-mono">
                      {balance.totals?.liability > balance.totals?.asset ? formatCurrency(balance.totals.liability - balance.totals.asset) : '-'}
                    </td>
                    <td className="border p-2 text-right font-mono">
                      {balance.totals?.loss > balance.totals?.gain ? formatCurrency(balance.totals.loss - balance.totals.gain) : '-'}
                    </td>
                    <td className="border p-2 text-right font-mono">
                      {balance.totals?.gain > balance.totals?.loss ? formatCurrency(balance.totals.gain - balance.totals.loss) : '-'}
                    </td>
                  </tr>
                  <tr className="bg-primary/10 font-bold border-t border-primary">
                    <td className="border p-2 text-right">TOTALES IGUALES</td>
                    <td className="border p-2" colSpan={4}></td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(Math.max(balance.totals?.asset, balance.totals?.liability))}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(Math.max(balance.totals?.asset, balance.totals?.liability))}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(Math.max(balance.totals?.loss, balance.totals?.gain))}</td>
                    <td className="border p-2 text-right font-mono">{formatCurrency(Math.max(balance.totals?.loss, balance.totals?.gain))}</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="income">
          <Card>
            <CardHeader>
              <CardTitle>Estado de Resultados</CardTitle>
              <CardDescription>Periodo: {startDate} al {endDate}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-3xl mx-auto">
              {/* Ingresos */}
              <div>
                <h3 className="font-bold text-lg text-green-600 border-b mb-2">Ingresos de Explotación</h3>
                {incomeStatement.income?.length === 0 && <p className="text-muted-foreground italic">No hay ingresos registrados</p>}
                {incomeStatement.income?.map((item, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-dashed">
                    <span>{item.name}</span>
                    <span className="font-mono">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-bold bg-green-50 mt-2 px-2 rounded">
                  <span>Total Ingresos</span>
                  <span>{formatCurrency(incomeStatement.total_income || 0)}</span>
                </div>
              </div>

              {/* Costos */}
              <div>
                <h3 className="font-bold text-lg text-orange-600 border-b mb-2">Costos de Explotación</h3>
                {incomeStatement.costs?.length === 0 && <p className="text-muted-foreground italic">No hay costos registrados</p>}
                {incomeStatement.costs?.map((item, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-dashed">
                    <span>{item.name}</span>
                    <span className="font-mono">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-bold bg-orange-50 mt-2 px-2 rounded">
                  <span>Total Costos</span>
                  <span>{formatCurrency(incomeStatement.total_costs || 0)}</span>
                </div>
              </div>

              {/* Margen Bruto */}
              <div className="flex justify-between py-3 font-bold text-xl bg-secondary px-4 rounded-lg border">
                <span>MARGEN BRUTO</span>
                <span>{formatCurrency(incomeStatement.gross_margin || 0)}</span>
              </div>

              {/* Gastos */}
              <div>
                <h3 className="font-bold text-lg text-red-600 border-b mb-2">Gastos de Administración y Ventas</h3>
                {incomeStatement.expenses?.length === 0 && <p className="text-muted-foreground italic">No hay gastos registrados</p>}
                {incomeStatement.expenses?.map((item, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-dashed">
                    <span>{item.name}</span>
                    <span className="font-mono">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-bold bg-red-50 mt-2 px-2 rounded">
                  <span>Total Gastos</span>
                  <span>{formatCurrency(incomeStatement.total_expenses || 0)}</span>
                </div>
              </div>

              {/* Resultado Final */}
              <div className={`flex justify-between py-4 font-bold text-2xl px-6 rounded-lg border-2 ${incomeStatement.net_income >= 0 ? 'bg-green-100 border-green-500 text-green-800' : 'bg-red-100 border-red-500 text-red-800'}`}>
                <span>RESULTADO DEL EJERCICIO</span>
                <span>{formatCurrency(incomeStatement.net_income || 0)}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ledger">
          {/* Existing Ledger View reused mainly */}
          <Card>
            <CardHeader>
              <CardTitle>Libro Mayor</CardTitle>
              <CardDescription>Detalle de todos los movimientos</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Cuenta</th>
                      <th className="text-left p-2">Descripción</th>
                      <th className="text-right p-2">Débito</th>
                      <th className="text-right p-2">Crédito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((entry, index) => (
                      <tr key={index} className="border-b hover:bg-secondary/50">
                        <td className="p-2 font-mono">{entry.date}</td>
                        <td className="p-2">
                          <div className="font-medium">{entry.account_code}</div>
                          <div className="text-xs text-muted-foreground">{entry.account_name}</div>
                        </td>
                        <td className="p-2">{entry.description}</td>
                        <td className="p-2 text-right font-mono">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}